import moment from "moment";
import React, { useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { LottieLoading } from "../../../components/graphics/LottieLoading";
import { useAppSelector } from "../../../redux/redux";
import {
  IAddInvoice,
  Invoice,
  useAddInvoiceMutation,
  useGetActiveTaxRateQuery,
  useGetInvoiceNoQuery,
  useGetInvoiceTermsQuery,
  useGetServicesQuery,
  useUpdateBilledStatusMutation,
} from "../../../services/BillingService";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import {
  useAddBilledWorkMutation,
  useGetUsersQuery,
} from "../../../slices/UserSlice";
import { SelectOptionType } from "../../../types/types";
import type { AiInvoiceState } from "../../ai/ask/actionProposal.utils";
import PayInvoiceModal from "../payments/PayInvoiceModal";
import {
  InvoiceFormInitialValues,
  InvoiceFormSubmitValues,
  InvoiceSaveOption,
} from "./form/invoice-form.types";
import {
  aiInvoiceValues,
  billedWorkRecords,
  createDefaultInvoiceItem,
  invoiceLineId,
  parseMoney,
  pickDefaultTaxRate,
} from "./form/invoice-form.utils";
import { InvoiceForm } from "./form/InvoiceForm";

const discountTypes = {
  "%": "89EDC863-9055-479B-AD08-554C0418A59F",
  $: "771B703A-5E94-4271-988A-206C0B48F7A8",
};

type AddInvoiceLocationState = {
  defaultCustomer?: SelectOptionType;
  /** Ask ProCharted "invoice …": dates, terms, notes and lines to start from. */
  aiInvoice?: AiInvoiceState["aiInvoice"];
};

const AddInvoice = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const locationState = location.state as AddInvoiceLocationState | null;
  const defaultCustomer = locationState?.defaultCustomer || null;
  const aiInvoice = locationState?.aiInvoice || null;

  const defaultCustomerId = searchParams.get("customerId") || "";
  const defaultWorkId = searchParams.get("workId") || "";
  const orgId = getLinkedFirm()?.orgId || "";

  const user = useAppSelector((state) => state.appReducer.user);

  const [invoiceForPayment, setInvoiceForPayment] = useState<Invoice | null>(
    null,
  );

  const [addInvoice, { isLoading: isLoadingAdd }] = useAddInvoiceMutation();
  const [updateBilledStatus] = useUpdateBilledStatusMutation();
  const [addBilledWork] = useAddBilledWorkMutation();

  const { data: terms, isLoading: isLoadingTerms } = useGetInvoiceTermsQuery({
    orgId,
  });

  const { data: invoiceNo, isLoading: isLoadingInvoiceNo } =
    useGetInvoiceNoQuery(orgId);

  const { data: services, isLoading: isLoadingServices } =
    useGetServicesQuery(orgId);

  const { data: taxRateData, isLoading: isLoadingTaxRate } =
    useGetActiveTaxRateQuery(orgId);

  const { data: users, isLoading: isLoadingUsers } = useGetUsersQuery(orgId);

  const sortedServices = useMemo(
    () =>
      [...(services || [])].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, {
          sensitivity: "base",
        }),
      ),
    [services],
  );

  const firstService = sortedServices[0];

  const defaultTerm = useMemo(
    () => terms?.find((item) => item?.termName === "Due on Receipt"),
    [terms],
  );

  const initialValues = useMemo<InvoiceFormInitialValues>(() => {
    const values: InvoiceFormInitialValues = {
      invoiceNo: Number(invoiceNo) || 0,
      currentContact: defaultCustomer,
      invoiceCustomers: [],
      invoiceDate: new Date(),
      dueDate: new Date(),
      term: defaultTerm?.id || "",
      selectedUserId: users?.find((item) => item.id === user.id)?.id || null,
      isIncludeTax: false,
      discount: "",
      discountType: "%",
      notes: "",
      taxRateValue: pickDefaultTaxRate(taxRateData),
      items: firstService ? [createDefaultInvoiceItem(firstService)] : [],
    };
    return aiInvoice
      ? aiInvoiceValues(values, aiInvoice, sortedServices)
      : values;
  }, [
    aiInvoice,
    defaultCustomer,
    defaultTerm?.id,
    firstService,
    invoiceNo,
    sortedServices,
    taxRateData,
    user.id,
    users,
  ]);

  const handleSubmit = async (
    values: InvoiceFormSubmitValues,
    saveOption: InvoiceSaveOption,
  ) => {
    const body: IAddInvoice = {
      isIncluded: values.isIncludeTax,
      notes: values.notes || "",
      orgId,
      createdByUserId: user.id,
      customerId: values.currentContact?.value || "",
      invoiceNo: values.invoiceNo,
      // Saved tax = sum of the lines' taxes (QA batch 17, was always 0).
      tax: +(values.totalTax || 0).toFixed(2),
      taxRateId: values.taxRateValue?.id || "",
      // "" = Custom date (no term); the server's TermId is a Guid or null.
      termId: values.term || null,
      discount: parseMoney(values.discount),
      discountTypeId: discountTypes[values.discountType].toLowerCase(),
      total: +values.total.toFixed(2),
      invoiceDate: moment(values.invoiceDate).startOf("day").toISOString(),
      assignedToUserId: values.selectedUserId || null,
      dueDate: moment(values.dueDate).startOf("day").toISOString(),
      invoiceCustomers: values.invoiceCustomers.map((item) => ({
        customerId: item.value,
      })),
      invoiceItem: values.items.map((item) => ({
        id: invoiceLineId(item),
        description: item.description,
        billingServiceId: item.service,
        amount: item.amount,
        rate: parseMoney(item.rate),
        quantity: item.qty,
        invoiceType: item.invoiceType,
        assignedUserId: item.assignedUserId,
        workTypeId: item.workTypeId,
        workId: item.workId,
        duration: item.duration,
        // BILL-039 (QA batch 17): each line keeps its own tax code.
        taxCodeId: item.taxCodeId || values.taxRateValue?.id || "",
      })),
    };

    const createdInvoice = await addInvoice(body).unwrap();
    const createdInvoiceId = createdInvoice?.id;

    if (!createdInvoiceId) {
      return;
    }

    if (values.billedWorks.length > 0) {
      await updateBilledStatus(
        values.billedWorks.map((item) => item.id),
      ).unwrap();

      await addBilledWork(
        billedWorkRecords(values.billedWorks, createdInvoiceId),
      ).unwrap();
    }

    if (saveOption === "pay") {
      setInvoiceForPayment(createdInvoice);
      return;
    }

    navigate(`/billing/invoices/${createdInvoiceId}`);
  };

  if (
    isLoadingTerms ||
    isLoadingServices ||
    isLoadingInvoiceNo ||
    isLoadingTaxRate ||
    isLoadingUsers
  ) {
    return <LottieLoading />;
  }

  if (
    !services ||
    !services.length ||
    !taxRateData ||
    !taxRateData.length ||
    !users
  ) {
    return (
      <div className={"text-center text-lg font-semibold"}>
        <div className={"text-center text-gray-800"}>
          Check your services and tax rate in settings
        </div>

        <div
          className={"link-primary cursor-pointer text-center hover:underline"}
          onClick={() => navigate("/settings/billing")}>
          Go to settings
        </div>
      </div>
    );
  }

  return (
    <>
      <InvoiceForm
        mode={"create"}
        formKey={`create-${invoiceNo}-${defaultTerm?.id}-${firstService?.id}-${
          pickDefaultTaxRate(taxRateData)?.id
        }-${defaultCustomer?.value || defaultCustomerId}-${defaultWorkId}`}
        title={"Create Invoice No."}
        submitLabel={"Save Invoice"}
        defaultCustomerId={defaultCustomerId}
        defaultWorkId={defaultWorkId}
        defaultAllTime={!!aiInvoice?.includeUnbilledTime && !defaultWorkId}
        initialValues={initialValues}
        terms={terms || []}
        services={sortedServices}
        taxRateData={taxRateData}
        users={users}
        isSubmitting={isLoadingAdd}
        showSaveAndPay
        onCancel={() => navigate(-1)}
        onSubmit={handleSubmit}
      />

      {invoiceForPayment && (
        <PayInvoiceModal
          close={() => setInvoiceForPayment(null)}
          invoice={invoiceForPayment}
        />
      )}
    </>
  );
};

export default AddInvoice;
